import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  getBillingStatus,
  getBillingPlans,
  createCheckoutSession,
  createPortalSession,
} from "../modules/upload-inbox/api";
import type { BillingStatus, BillingPlan } from "../modules/upload-inbox/api";
import { useOrganization } from "../context/OrganizationContext";
import "./BillingPage.css";

// Shown when the Stripe product has no marketing features set.
const DEFAULT_FEATURES = [
  "Unlimited document storage",
  "AI-powered document review",
  "Team collaboration",
  "Priority support",
];

function formatPrice(plan: BillingPlan): string {
  const amount = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: plan.currency.toUpperCase(),
    // Whole amounts render as "$19", not "$19.00".
    minimumFractionDigits: plan.unitAmount % 100 === 0 ? 0 : 2,
  }).format(plan.unitAmount / 100);
  const per = plan.intervalCount === 1 ? plan.interval : `${plan.intervalCount} ${plan.interval}s`;
  return `${amount} / ${per}`;
}

/** One card per product, prices in catalogue order. */
function groupByProduct(plans: BillingPlan[]): BillingPlan[][] {
  const groups = new Map<string, BillingPlan[]>();
  for (const plan of plans) {
    const group = groups.get(plan.productId);
    if (group) group.push(plan);
    else groups.set(plan.productId, [plan]);
  }
  return [...groups.values()];
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  active: { label: "Active", className: "billing-badge--active" },
  trialing: { label: "Trial", className: "billing-badge--active" },
  incomplete: { label: "Payment pending", className: "billing-badge--past-due" },
  past_due: { label: "Past Due", className: "billing-badge--past-due" },
  canceled_active: { label: "Canceled (active until period end)", className: "billing-badge--canceled-active" },
  free: { label: "Free", className: "billing-badge--free" },
};

const PAID_STATUSES: BillingStatus["effectiveStatus"][] = ["active", "trialing", "past_due", "canceled_active"];

export function BillingPage() {
  const { activeOrganization } = useOrganization();
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [plans, setPlans] = useState<BillingPlan[] | null>(null);
  const [plansError, setPlansError] = useState(false);
  const [pendingPriceId, setPendingPriceId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // Back from Stripe Checkout, the subscription webhook can land a few
    // seconds after the redirect: poll briefly so the page (and, for a new
    // org, access to the app) updates without a manual refresh.
    const returnedFromCheckout = new URLSearchParams(window.location.search).has("success");
    let attempts = 0;
    const load = () =>
      getBillingStatus()
        .then((s) => {
          if (cancelled) return;
          setStatus(s);
          setLoading(false);
          const paid = PAID_STATUSES.includes(s.effectiveStatus);
          if (returnedFromCheckout && !paid && attempts++ < 20) timer = setTimeout(load, 3000);
        })
        .catch((err) => {
          if (cancelled) return;
          setError(err instanceof Error ? err.message : "Failed to load billing");
          setLoading(false);
        });
    load();
    // Loaded separately so a Stripe outage only hides the plan cards, not
    // the subscription status.
    getBillingPlans()
      .then((res) => { setPlans(res.plans); setPlansError(false); })
      .catch(() => { setPlans([]); setPlansError(true); });
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [activeOrganization?.id]);

  async function handleSubscribe(priceId: string) {
    setActionLoading(true);
    setPendingPriceId(priceId);
    setActionError(null);
    try {
      const { url } = await createCheckoutSession(priceId);
      window.location.href = url;
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to start checkout");
      setActionLoading(false);
      setPendingPriceId(null);
    }
  }

  async function handleManageBilling() {
    setActionLoading(true);
    setActionError(null);
    try {
      const { url } = await createPortalSession();
      window.location.href = url;
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to open billing portal");
      setActionLoading(false);
    }
  }

  const params = new URLSearchParams(window.location.search);
  const successMessage = params.get("success") ? "Your subscription is now active. Welcome to Haven Pro!" : null;
  const cancelMessage = params.get("canceled") ? "Checkout was canceled — your plan hasn't changed." : null;

  const badge = status ? STATUS_LABELS[status.effectiveStatus] : null;
  const isPaid = status ? PAID_STATUSES.includes(status.effectiveStatus) : false;
  // A new org that hasn't subscribed yet: the app is locked until it does.
  const choosingFirstPlan = Boolean(status?.subscriptionRequired);
  // First payment hasn't cleared (e.g. awaiting 3-D Secure). Not paid yet,
  // but a subscription exists — don't offer a second checkout.
  const isPaymentPending = status?.effectiveStatus === "incomplete";

  return (
    <div className="billing-page">
      <h2 className="billing-title">
        {choosingFirstPlan ? "Choose a plan to get started" : "Billing & Plan"}
      </h2>
      {choosingFirstPlan && (
        <p className="billing-intro">
          Pick a plan to start uploading and organizing your documents. You can
          change or cancel it at any time.
        </p>
      )}

      {successMessage && isPaid && (
        <div className="billing-banner billing-banner--success">
          {successMessage}{" "}
          <Link to="/documents" className="billing-banner-link">Go to your documents</Link>
        </div>
      )}
      {successMessage && !isPaid && status && (
        <div className="billing-banner billing-banner--info">
          Payment received — confirming your subscription…
        </div>
      )}
      {cancelMessage && <div className="billing-banner billing-banner--info">{cancelMessage}</div>}

      {loading && <p className="billing-loading">Loading billing information…</p>}
      {error && <div className="billing-error">{error}</div>}

      {!loading && !error && status && (
        <>
          <div className="billing-card">
            <div className="billing-card-row">
              <span className="billing-card-label">Plan</span>
              <span className="billing-card-value">{status.billingPlan
                  ? status.billingPlanLabel ?? status.billingPlan.replace(/_/g, " ")
                  : status.plan ? status.plan.replace(/_/g, " ") : "Free"}</span>
            </div>
            <div className="billing-card-row">
              <span className="billing-card-label">Status</span>
              {badge && (
                <span className={`billing-badge ${badge.className}`}>{badge.label}</span>
              )}
            </div>
            <div className="billing-card-row">
              <span className="billing-card-label">Credits remaining</span>
              <span className="billing-card-value">
                {status.creditBalance ?? 0}
                {(status.creditBalance ?? 0) < 1 && (
                  <span className="billing-badge billing-badge--past-due" style={{ marginLeft: "0.5rem" }}>
                    Out of credits
                  </span>
                )}
              </span>
            </div>
            {status.currentPeriodEnd && (
              <div className="billing-card-row">
                <span className="billing-card-label">
                  {status.subscriptionStatus === "canceled" ? "Access until" : "Renews"}
                </span>
                <span className="billing-card-value">{formatDate(status.currentPeriodEnd)}</span>
              </div>
            )}
          </div>

          {status.effectiveStatus === "past_due" && (
            <div className="billing-alert">
              Your last payment failed. Please update your payment method to avoid service interruption.
            </div>
          )}

          {isPaymentPending && (
            <div className="billing-alert">
              Your first payment hasn't been confirmed yet. If your bank asked for
              verification, complete it from the email Stripe sent you, or update
              your payment method below.
            </div>
          )}

          <div className="billing-actions">
            {!isPaid && !isPaymentPending ? (
              <div className="billing-plan-list">
                {plans === null && <p className="billing-loading">Loading plans…</p>}
                {plans?.length === 0 && (
                  <div className="billing-error">
                    {plansError
                      ? "Plans couldn't be loaded. Please try again shortly."
                      : "No plans are available right now. Contact support."}
                  </div>
                )}
                {plans && groupByProduct(plans).map((group) => (
                  <div className="billing-plan-card" key={group[0].productId}>
                    <div className="billing-plan-name">{group[0].productName}</div>
                    <ul className="billing-plan-features">
                      {(group[0].features.length ? group[0].features : DEFAULT_FEATURES).map((f) => (
                        <li key={f}>{f}</li>
                      ))}
                    </ul>
                    <div className="billing-plan-prices">
                      {group.map((plan) => (
                        <button
                          key={plan.priceId}
                          className="billing-btn billing-btn--primary"
                          onClick={() => handleSubscribe(plan.priceId)}
                          disabled={actionLoading}
                          aria-label={`Subscribe to ${plan.label} at ${formatPrice(plan)}`}
                        >
                          {pendingPriceId === plan.priceId
                            ? "Redirecting…"
                            : group.length > 1
                              ? `${plan.label} — ${formatPrice(plan)}`
                              : `Subscribe — ${formatPrice(plan)}`}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <button
                className="billing-btn billing-btn--secondary"
                onClick={handleManageBilling}
                disabled={actionLoading}
              >
                {actionLoading ? "Redirecting…" : "Manage Billing"}
              </button>
            )}
          </div>

          {actionError && <div className="billing-error">{actionError}</div>}
        </>
      )}
    </div>
  );
}
